import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getAllCompanies, createCompany, deleteCompany, setCompanyPaused, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useFieldErrors } from '../lib/useFieldErrors';
import { FieldError } from '../components/FieldError';
import { 
    Search, 
    Building2, 
    Users,
    Film,
    HardDrive,
    ChevronRight,
    RefreshCw,
    Crown,
    Plus,
    Trash2,
    Loader2,
    Pause,
    Play
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function CompanySelector() {
    const [companies, setCompanies] = useState([]);
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [switching, setSwitching] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteCompanyId, setDeleteCompanyId] = useState(null);
    const [pauseCompanyId, setPauseCompanyId] = useState(null);
    const [togglingPause, setTogglingPause] = useState(null);
    const [creating, setCreating] = useState(false);
    const { errors, fail, clearError, resetErrors, fieldProps } = useFieldErrors();
    const [formData, setFormData] = useState({
        company_name: '',
        admin_name: '',
        admin_email: '',
        admin_password: ''
    });
    const { switchCompany, activeCompany } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (search) {
            setFilteredCompanies(
                companies.filter(c => 
                    c.name.toLowerCase().includes(search.toLowerCase())
                )
            );
        } else {
            setFilteredCompanies(companies);
        }
    }, [search, companies]);

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            const response = await getAllCompanies();
            setCompanies(response.data);
            setFilteredCompanies(response.data);
            
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement des entreprises'));
        } finally {
            setLoading(false);
        }
    };

    const handleSelectCompany = async (company) => {
        setSwitching(company.id);
        try {
            await switchCompany(company.id, company.name);
            toast.success(`Vous gérez maintenant : ${company.name}`);
            navigate('/dashboard');
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors du changement d\'entreprise'));
        } finally {
            setSwitching(null);
        }
    };

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        resetErrors();
        
        if (!formData.company_name.trim()) {
            fail('company_name', 'Le nom de la société est requis');
            return;
        }
        if (!formData.admin_name.trim()) {
            fail('admin_name', 'Le nom de l\'administrateur est requis');
            return;
        }
        if (!formData.admin_email.trim()) {
            fail('admin_email', 'L\'email de l\'administrateur est requis');
            return;
        }
        if (!formData.admin_password || formData.admin_password.length < 8) {
            fail('admin_password', 'Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        setCreating(true);
        try {
            const response = await createCompany(formData);
            toast.success(`Client "${response.data.company.name}" créé avec succès`);
            setCreateDialogOpen(false);
            setFormData({ company_name: '', admin_name: '', admin_email: '', admin_password: '' });
            fetchCompanies();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la création'));
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteCompany = async () => {
        if (!deleteCompanyId) return;
        
        const company = companies.find(c => c.id === deleteCompanyId);
        
        try {
            await deleteCompany(deleteCompanyId);
            toast.success(`Client "${company?.name}" supprimé`);
            setDeleteCompanyId(null);
            fetchCompanies();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la suppression'));
        }
    };

    const handleTogglePause = async (company, paused) => {
        setTogglingPause(company.id);
        try {
            await setCompanyPaused(company.id, paused);
            setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, paused } : c));
            toast.success(paused ? `Client "${company.name}" mis en pause` : `Client "${company.name}" réactivé`);
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors du changement de statut'));
        } finally {
            setTogglingPause(null);
            setPauseCompanyId(null);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        clearError(name);
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="company-selector-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <Crown className="w-6 h-6 text-primary" />
                            <h1 className="font-heading text-3xl font-bold tracking-tight">
                                GESTION DES CLIENTS
                            </h1>
                        </div>
                        <p className="text-muted-foreground mt-1">
                            Créez et gérez les comptes de vos clients
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={fetchCompanies} disabled={loading}>
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Actualiser
                        </Button>
                        <Button onClick={() => { resetErrors(); setCreateDialogOpen(true); }} data-testid="create-company-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau client
                        </Button>
                    </div>
                </div>

                {/* Active Company Banner */}
                {activeCompany && (
                    <Card className="bg-primary/10 border-primary/30">
                        <CardContent className="py-4">
                            <div className="flex items-center gap-3">
                                <Building2 className="w-5 h-5 text-primary" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Client actuellement géré :</p>
                                    <p className="font-semibold text-primary">
                                        {companies.find(c => c.id === activeCompany.id)?.name || activeCompany.name || 'Aucun'}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Rechercher un client..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="company-search-input"
                    />
                </div>

                {/* Companies Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...Array(6)].map((_, i) => (
                            <Skeleton key={i} className="h-48" />
                        ))}
                    </div>
                ) : filteredCompanies.length === 0 ? (
                    <Card className="bg-card border-border">
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <Building2 className="w-16 h-16 text-muted-foreground/50 mb-4" />
                            <h3 className="font-heading text-xl font-semibold mb-2">
                                Aucun client
                            </h3>
                            <p className="text-muted-foreground text-center max-w-md mb-6">
                                {search ? 'Aucun client ne correspond à votre recherche.' : 'Créez votre premier client pour commencer.'}
                            </p>
                            {!search && (
                                <Button onClick={() => { resetErrors(); setCreateDialogOpen(true); }}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Créer un client
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredCompanies.map((company) => {
                            const isActive = activeCompany?.id === company.id;
                            
                            return (
                                <Card 
                                    key={company.id} 
                                    className={`bg-card border-border hover:border-primary/50 transition-[opacity,border-color,box-shadow] duration-300 group ${
                                        isActive ? 'ring-2 ring-primary' : ''
                                    } ${company.paused ? 'opacity-70' : ''}`}
                                    data-testid={`company-card-${company.id}`}
                                >
                                    <CardHeader className="pb-2">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded bg-primary/20 text-primary font-bold" aria-hidden="true">
                                                    {company.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <CardTitle className="font-heading text-lg font-semibold group-hover:text-primary transition-colors break-words">
                                                        {company.name}
                                                    </CardTitle>
                                                    <p className="text-xs text-muted-foreground">
                                                        Créé le {format(new Date(company.created_at), 'dd MMM yyyy', { locale: fr })}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {isActive && (
                                                    <Badge className="hidden sm:inline-flex bg-primary text-primary-foreground text-xs">
                                                        Actif
                                                    </Badge>
                                                )}
                                                {company.paused && (
                                                    <Badge variant="outline" className="text-xs border-primary/50 text-primary">
                                                        En pause
                                                    </Badge>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon" aria-label={company.paused ? `Réactiver le client ${company.name}` : `Mettre en pause le client ${company.name}`}
                                                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                    disabled={togglingPause === company.id}
                                                    title={company.paused ? 'Réactiver ce client' : 'Mettre ce client en pause'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (company.paused) {
                                                            handleTogglePause(company, false);
                                                        } else {
                                                            setPauseCompanyId(company.id);
                                                        }
                                                    }}
                                                    data-testid={`pause-company-${company.id}`}
                                                >
                                                    {togglingPause === company.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : company.paused ? (
                                                        <Play className="w-4 h-4" />
                                                    ) : (
                                                        <Pause className="w-4 h-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon" aria-label={`Supprimer le client ${company.name}`}
                                                    className="h-8 w-8 text-muted-foreground hover:text-destructive-text"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteCompanyId(company.id);
                                                    }}
                                                    data-testid={`delete-company-${company.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-3 gap-2 mb-4">
                                            <div className="text-center p-2 rounded bg-secondary/30">
                                                <Film className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                                                <p className="font-bold text-lg">{company.total_projects ?? 0}</p>
                                                <p className="text-xs text-muted-foreground">Projets</p>
                                            </div>
                                            <div className="text-center p-2 rounded bg-secondary/30">
                                                <HardDrive className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                                                <p className="font-bold text-lg">{company.total_masters ?? 0}</p>
                                                <p className="text-xs text-muted-foreground">Masters</p>
                                            </div>
                                            <div className="text-center p-2 rounded bg-secondary/30">
                                                <Users className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                                                <p className="font-bold text-lg">{company.total_users ?? 0}</p>
                                                <p className="text-xs text-muted-foreground">Users</p>
                                            </div>
                                        </div>
                                        <Button 
                                            className="w-full" 
                                            variant={isActive ? "secondary" : "default"}
                                            disabled={switching === company.id}
                                            onClick={() => handleSelectCompany(company)}
                                        >
                                            {switching === company.id ? (
                                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <ChevronRight className="w-4 h-4 mr-2" />
                                            )}
                                            {isActive ? 'Client actif' : 'Gérer ce client'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create Company Dialog */}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-primary" />
                            Nouveau client
                        </DialogTitle>
                        <DialogDescription>
                            Créez un nouveau compte client avec son administrateur
                        </DialogDescription>
                    </DialogHeader>
                    <form noValidate onSubmit={handleCreateCompany} className="space-y-6">
                        {/* Company Info */}
                        <div className="space-y-4">
                            <div className="pb-2 border-b border-border">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                                    Informations société
                                </h4>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="company_name">Nom de la société *</Label>
                                <Input
                                    id="company_name"
                                    {...fieldProps('company_name')}
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleFormChange}
                                    placeholder="Ex: Studio Production XYZ"
                                    required
                                    data-testid="company-name-input"
                                />
                                <FieldError id="company_name" message={errors['company_name']} />
                            </div>
                        </div>

                        {/* Admin Info */}
                        <div className="space-y-4">
                            <div className="pb-2 border-b border-border">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                                    Administrateur du compte
                                </h4>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="admin_name">Nom de l'administrateur *</Label>
                                <Input
                                    id="admin_name"
                                    {...fieldProps('admin_name')}
                                    name="admin_name"
                                    value={formData.admin_name}
                                    onChange={handleFormChange}
                                    placeholder="Ex: Jean Dupont"
                                    required
                                    data-testid="admin-name-input"
                                />
                                <FieldError id="admin_name" message={errors['admin_name']} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="admin_email">Email *</Label>
                                <Input
                                    id="admin_email"
                                    autoComplete="off"
                                    {...fieldProps('admin_email')}
                                    name="admin_email"
                                    type="email"
                                    value={formData.admin_email}
                                    onChange={handleFormChange}
                                    placeholder="admin@societe.com"
                                    required
                                    data-testid="admin-email-input"
                                />
                                <FieldError id="admin_email" message={errors['admin_email']} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="admin_password">Mot de passe *</Label>
                                <Input
                                    id="admin_password"
                                    autoComplete="new-password"
                                    {...fieldProps('admin_password')}
                                    name="admin_password"
                                    type="password"
                                    value={formData.admin_password}
                                    onChange={handleFormChange}
                                    placeholder="Minimum 8 caractères"
                                    required
                                    data-testid="admin-password-input"
                                />
                                <FieldError id="admin_password" message={errors['admin_password']} />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => setCreateDialogOpen(false)}
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={creating} data-testid="create-company-submit">
                                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Créer le client
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Pause Company Dialog */}
            <AlertDialog open={!!pauseCompanyId} onOpenChange={() => setPauseCompanyId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Mettre ce client en pause ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Les utilisateurs de <span className="font-semibold text-foreground">{companies.find(c => c.id === pauseCompanyId)?.name}</span> seront
                            déconnectés et ne pourront plus se connecter tant que le client est en pause.
                            <br /><br />
                            Aucune donnée n'est supprimée : vous pourrez réactiver le client à tout moment.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                const company = companies.find(c => c.id === pauseCompanyId);
                                if (company) handleTogglePause(company, true);
                            }}
                        >
                            Mettre en pause
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Company Dialog */}
            <AlertDialog open={!!deleteCompanyId} onOpenChange={() => setDeleteCompanyId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-destructive-text">
                            Supprimer ce client ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-semibold text-foreground">
                                Attention : Cette action est irréversible !
                            </span>
                            <br /><br />
                            Toutes les données du client seront supprimées :
                            <ul className="list-disc list-inside mt-2 space-y-1">
                                <li>Tous les projets</li>
                                <li>Tous les masters</li>
                                <li>Tous les mouvements</li>
                                <li>Tous les utilisateurs</li>
                            </ul>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDeleteCompany} 
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Supprimer définitivement
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
